import { useCallback, useEffect, useState } from "react"

import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Skeleton,
  Typography,
} from "@mui/material"
import { type GridColDef } from "@mui/x-data-grid"
import { useNavigate } from "react-router-dom"
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts"

import {
  handleApiError,
  pipelineSlaService,
  type PipelineSlaResponse,
  type PipelineStage,
  type SlaOrderEntry,
  type SlaStatus,
  type StageMetrics,
} from "../api"
import { AdminDataGrid } from "../components/admin-data-grid"
import { DateCell, LinkCell } from "../components/data-grid-cells"
import { truncateId } from "../components/data-grid-utils"

const SLA_STATUS_COLORS: Record<SlaStatus, "error" | "warning" | "success"> = {
  breaching: "error",
  approaching: "warning",
  on_track: "success",
}

const SLA_STATUS_LABELS: Record<SlaStatus, string> = {
  breaching: "Breaching",
  approaching: "Approaching",
  on_track: "On Track",
}

const formatHours = (hours: number): string => {
  if (hours < 1) return `${Math.round(hours * 60)}m`
  if (hours < 24) return `${Math.round(hours * 10) / 10}h`
  const days = Math.floor(hours / 24)
  const remainingHours = Math.round(hours % 24)
  if (remainingHours === 0) return `${days}d`
  return `${days}d ${remainingHours}h`
}

const formatSlaTarget = (hours: number): string => {
  if (hours < 24) return `${hours}h`
  return `${hours / 24}d`
}

const columns: GridColDef<SlaOrderEntry>[] = [
  {
    field: "email",
    headerName: "Email",
    flex: 1.5,
    minWidth: 200,
    renderCell: (params) => (
      <LinkCell to={`/users/${params.row.userId}`}>
        {params.value}
      </LinkCell>
    ),
  },
  {
    field: "firstName",
    headerName: "First Name",
    flex: 0.7,
    minWidth: 100,
    valueGetter: (_value, row) => row.firstName ?? "-",
  },
  {
    field: "lastName",
    headerName: "Last Name",
    flex: 0.7,
    minWidth: 100,
    valueGetter: (_value, row) => row.lastName ?? "-",
  },
  {
    field: "orderId",
    headerName: "Order",
    flex: 0.8,
    minWidth: 120,
    renderCell: (params) => (
      <LinkCell to={`/orders/${params.value}`} monospace>
        {truncateId(String(params.value))}
      </LinkCell>
    ),
  },
  {
    field: "orderStatus",
    headerName: "Status",
    width: 130,
    renderCell: (params) => (
      <Chip
        label={String(params.value).replace(/_/g, " ").toUpperCase()}
        size="small"
        variant="outlined"
        sx={{ fontSize: "0.75rem", height: 24 }}
      />
    ),
  },
  {
    field: "hoursInStage",
    headerName: "Time in Stage",
    width: 130,
    renderCell: (params) => {
      const hours = params.value as number
      const status = params.row.slaStatus
      return (
        <Chip
          label={formatHours(hours)}
          size="small"
          color={SLA_STATUS_COLORS[status]}
          variant="outlined"
          sx={{ fontSize: "0.75rem", height: 24, fontWeight: 600 }}
        />
      )
    },
  },
  {
    field: "slaStatus",
    headerName: "SLA",
    width: 110,
    renderCell: (params) => {
      const status = params.value as SlaStatus
      return (
        <Chip
          label={SLA_STATUS_LABELS[status]}
          size="small"
          color={SLA_STATUS_COLORS[status]}
          sx={{ fontSize: "0.75rem", height: 24, fontWeight: 600 }}
        />
      )
    },
  },
  {
    field: "stageEnteredAt",
    headerName: "Stage Entered",
    flex: 0.8,
    minWidth: 140,
    renderCell: (params) => <DateCell value={params.value as string} />,
  },
]

function getStageBorderColor(stage: PipelineStage): string {
  if (stage.breachingCount > 0) return "error.main"
  if (stage.approachingCount > 0) return "warning.main"
  return "divider"
}

function StageCard({ stage, onToggle, expanded }: {
  stage: PipelineStage
  onToggle: () => void
  expanded: boolean
}) {
  const totalOrders = stage.breachingCount + stage.approachingCount + stage.onTrackCount
  const hasAlert = stage.breachingCount > 0 || stage.approachingCount > 0
  const navigate = useNavigate()

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: getStageBorderColor(stage),
        borderWidth: hasAlert ? 2 : 1,
      }}
    >
      <CardContent
        sx={{ p: 2, "&:last-child": { pb: 2 }, cursor: "pointer" }}
        onClick={onToggle}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {stage.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              SLA target: {formatSlaTarget(stage.slaHours)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
            {stage.breachingCount > 0 && (
              <Chip
                label={`${stage.breachingCount} breaching`}
                size="small"
                color="error"
                sx={{ fontSize: "0.75rem", fontWeight: 600 }}
              />
            )}
            {stage.approachingCount > 0 && (
              <Chip
                label={`${stage.approachingCount} approaching`}
                size="small"
                color="warning"
                sx={{ fontSize: "0.75rem", fontWeight: 600 }}
              />
            )}
            {stage.onTrackCount > 0 && (
              <Chip
                label={`${stage.onTrackCount} on track`}
                size="small"
                color="success"
                variant="outlined"
                sx={{ fontSize: "0.75rem" }}
              />
            )}
            {totalOrders === 0 && (
              <Chip
                label="No orders"
                size="small"
                color="default"
                variant="outlined"
                sx={{ fontSize: "0.75rem" }}
              />
            )}
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {stage.description}
        </Typography>
      </CardContent>

      <Collapse in={expanded}>
        {stage.orders.length > 0 && (
          <CardContent sx={{ p: 0, pt: 0 }}>
            <AdminDataGrid<SlaOrderEntry>
              rows={stage.orders}
              columns={columns}
              getRowId={(row) => row.orderId}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
                sorting: { sortModel: [{ field: "hoursInStage", sort: "desc" }] },
              }}
              density="compact"
              onRowClick={(params) => {
                navigate(`/users/${params.row.userId}`)
              }}
              sx={{
                height: Math.min(400, 52 + stage.orders.length * 36),
                minHeight: 150,
                "& .MuiDataGrid-columnHeaders": { fontWeight: 600 },
              }}
            />
          </CardContent>
        )}
      </Collapse>
    </Card>
  )
}

function MetricsSection({ metrics }: { metrics: StageMetrics[] }) {
  const hasAnyData = metrics.some((m) => m.completedCount > 0)

  if (!hasAnyData) {
    return (
      <Card variant="outlined" sx={{ p: 4, textAlign: "center", bgcolor: "grey.50" }}>
        <Typography variant="body1" color="text.secondary" fontWeight={500}>
          No historical data yet
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Metrics will appear once orders complete each pipeline stage
        </Typography>
      </Card>
    )
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {metrics.map((m) => (
          <Card key={m.stageId} variant="outlined" sx={{ flex: "1 1 0", minWidth: 220 }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                {m.label}
              </Typography>
              {m.completedCount === 0 ? (
                <Typography variant="body2" color="text.secondary">No data</Typography>
              ) : (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Median</Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {m.medianHours != null ? formatHours(m.medianHours) : "-"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">P95</Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {m.p95Hours != null ? formatHours(m.p95Hours) : "-"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Completed</Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {m.completedCount}
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      {metrics.map((m) => {
        if (m.weeklyTrend.length < 2) return null
        const chartData = m.weeklyTrend.map((w) => ({
          week: new Date(w.weekStart).toLocaleDateString("en-GB", { month: "short", day: "numeric" }),
          median: Math.round(w.medianHours * 10) / 10,
          p95: Math.round(w.p95Hours * 10) / 10,
          count: w.completedCount,
        }))

        return (
          <Card key={m.stageId} variant="outlined">
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                {m.label} - Weekly Trend (hours)
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <RechartsTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="median" stroke="#2196f3" name="Median (hrs)" strokeWidth={2} />
                  <Line type="monotone" dataKey="p95" stroke="#f44336" name="P95 (hrs)" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )
      })}
    </Box>
  )
}

export function PipelineSlaSection() {
  const [data, setData] = useState<PipelineSlaResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const [expandedStages, setExpandedStages] = useState<Set<string>>(new Set())

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true)
      setError("")
      const response = await pipelineSlaService.getDashboard()
      setData(response)

      const breachingStageIds = response.stages
        .filter((s) => s.breachingCount > 0 || s.approachingCount > 0)
        .map((s) => s.id)
      setExpandedStages(new Set(breachingStageIds))
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const toggleStage = (stageId: string) => {
    setExpandedStages((prev) => {
      const next = new Set(prev)
      if (next.has(stageId)) {
        next.delete(stageId)
      } else {
        next.add(stageId)
      }
      return next
    })
  }

  if (isLoading && !data) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
        ))}
      </Box>
    )
  }

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {data && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
              Current Pipeline Status
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {data.stages.map((stage) => (
                <StageCard
                  key={stage.id}
                  stage={stage}
                  expanded={expandedStages.has(stage.id)}
                  onToggle={() => toggleStage(stage.id)}
                />
              ))}
            </Box>
          </Box>

          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
              Historical Metrics (last 12 weeks)
            </Typography>
            <MetricsSection metrics={data.metrics} />
          </Box>
        </Box>
      )}
    </>
  )
}
